/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useCallback, useMemo } from "react";
import { Typography, Button, Dialog, Menu, MenuItem, DialogContent } from "@material-ui/core";
import SpeakerNotesIcon from "@material-ui/icons/SpeakerNotes";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import { LightAsync as SyntaxHighlighter } from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { CopyButton } from "../../agentConsole/components/Common/CopyButton";

/**
 * Generate initial Lex interactive message list picker template
 * @param {string} key interactive message key e.g. FastFoodBot_TestBotAlias_en_US_OrderBurger_DrinkSlot
 * @param {{slotTypeId:string}} slot intent slot
 * @param {Array<{slotType:{slotTypeId:string,slotTypeValues:Array<{sampleValue:{value:string}}>}}>} slotTypes
 * @returns interactive message json to be pasted into WebChatInteractiveMessageTable dynamo table
 */
const generateListPickerTemplate = (key, slot, slotTypes) => {
	const slotTypeValues =
		slotTypes.find((x) => x.slotType.slotTypeId === slot.slotTypeId)?.slotType?.slotTypeValues || [];

	const items = slotTypeValues.map((x) => ({ value: x.sampleValue.value }));

	const templateJson = {
		key,
		templateJson: {
			data: {
				content: {
					title: "<Insert your message title here (required)>",
					elements: items.map((e) => ({
						title: e.value,
					})),
				},
			},
			templateType: "ListPicker",
			version: "1.0",
		},
	};

	return JSON.stringify(templateJson, null, 2);
};

/**
 * Generate initial Lex interactive message time picker template
 * @param {string} key interactive message key e.g. FastFoodBot_TestBotAlias_en_US_OrderBurger_DrinkSlot
 * @param {{slotTypeId:string}} slot intent slot
 * @param {Array<{slotType:{slotTypeId:string,slotTypeValues:Array<{sampleValue:{value:string}}>}}>} slotTypes
 * @returns interactive message json to be pasted into WebChatInteractiveMessageTable dynamo table
 */
const generateTimePickerTemplate = (key, slot, slotTypes) => {
	const slotTypeValues =
		slotTypes.find((x) => x.slotType.slotTypeId === slot.slotTypeId)?.slotType?.slotTypeValues || [];

	const items = slotTypeValues.map((x) => ({ value: x.sampleValue.value }));

	const templateJson = {
		key,
		templateJson: {
			data: {
				content: {
					title: "<Insert your message title here (required)>",
					timeslots: items.map((e) => ({
						date: e.value,
						duration: 60,
					})),
				},
			},
			templateType: "TimePicker",
			version: "1.0",
		},
	};

	return JSON.stringify(templateJson, null, 2);
};

const templateTypes = {
	LIST: {
		id: "LIST",
		label: "List picker",
	},
	TIME: {
		id: "TIME",
		label: "Time picker",
	},
};

/**
 * Modal component to view and copy interactive messages for an intent
 * @param {Object} props component props
 * @param {string} props.intentName name of the intent
 * @param {string} props.interactiveMessageKey interactive message key e.g. FastFoodBot_TestBotAlias_en_US_OrderBurger_DrinkSlot
 * @param {{slotTypeId:string}} props.slot intent slot
 * @param {Array<{slotType:{slotTypeId:string,slotTypeValues:Array<{sampleValue:{value:string}}>}}>} props.slotTypes
 */
export const InteractiveMessageModal = ({ interactiveMessageKey, slot, slotTypes }) => {
	const [open, setOpen] = useState(false);
	const [menuAnchor, setMenuAnchor] = useState(null);
	const [selectedTemplateType, setSelectedTemplateType] = useState(templateTypes.LIST);

	const handleMenuClose = useCallback(() => {
		setMenuAnchor(null);
	}, []);

	const templateJson = useMemo(() => {
		if (selectedTemplateType.id === templateTypes.LIST.id) {
			return generateListPickerTemplate(interactiveMessageKey, slot, slotTypes);
		}
		if (selectedTemplateType.id === templateTypes.TIME.id) {
			return generateTimePickerTemplate(interactiveMessageKey, slot, slotTypes);
		}

		return "";
	}, [selectedTemplateType, interactiveMessageKey, slot, slotTypes]);

	return (
		<div>
			<Button variant="outlined" size="small" onClick={() => setOpen(true)} startIcon={<SpeakerNotesIcon />}>
				Interactive Messages
			</Button>

			<Dialog
				fullWidth
				maxWidth="md"
				open={open}
				onClose={() => {
					setOpen(false);
				}}
			>
				<AppBar color="default" elevation={2} position="sticky" style={{ padding: 0 }}>
					<Toolbar style={{ justifyContent: "space-between" }}>
						<div style={{ width: 145 }}>
							<IconButton
								edge="start"
								color="inherit"
								style={{ float: "left" }}
								size="small"
								onClick={() => {
									setOpen(false);
								}}
								aria-label="close"
							>
								<CloseIcon />
							</IconButton>
						</div>
						<Typography
							variant="h6"
							style={{ fontSize: "1.2em" }}
						>{`${slot.slotName} Interactive Message Templates`}</Typography>
						<div>
							<Button
								style={{ textTransform: "none", minWidth: 120, padding: 5 }}
								variant="outlined"
								onClick={(e) => setMenuAnchor(e.currentTarget)}
								endIcon={<ArrowDropDownIcon />}
							>
								{selectedTemplateType.label}
							</Button>
							<Menu anchorEl={menuAnchor} keepMounted open={Boolean(menuAnchor)} onClose={handleMenuClose}>
								{Object.keys(templateTypes).map((key) => (
									<MenuItem
										key={key}
										onClick={() => {
											setSelectedTemplateType(templateTypes[key]);
											handleMenuClose();
										}}
									>
										{templateTypes[key].label}
									</MenuItem>
								))}
							</Menu>
						</div>
					</Toolbar>
				</AppBar>
				<DialogContent style={{ padding: 0 }}>
					<div style={{ position: "relative" }}>
						<div style={{ position: "absolute", top: "1.5em", right: "2em" }}>
							<CopyButton
								value={templateJson}
								title="Copy interactive message template"
								style={{ fontSize: "1.2em", color: "#707070" }}
							/>
						</div>
						<SyntaxHighlighter
							language="json"
							style={docco}
							customStyle={{ marginBottom: 0, padding: "1.5em 2em", height: "100%" }}
						>
							{templateJson}
						</SyntaxHighlighter>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};
